import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Send, Image, ArrowLeft, ShieldAlert, MoreVertical, Flag, Ban } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const REPORT_REASONS = [
  { value: 'scam', label: 'Scam or phishing' },
  { value: 'harassment', label: 'Harassment or abuse' },
  { value: 'spam', label: 'Spam' },
  { value: 'other', label: 'Something else' },
];
import Layout from '../components/layout/Layout';
import { useApp } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const MessagesPage = () => {
  const navigate = useNavigate();
  const { conversationId } = useParams();
  const { conversations, messages, products, user, isAuthenticated, getUserByIdSync, sendMessage, loadConversationMessages, refreshConversations, typingConversations, sendTypingSignal } = useApp();
  const [newMessage, setNewMessage] = useState('');
  const messagesEndRef = useRef(null);
  const lastTypingRef = useRef(0);
  const [, setTypingTick] = useState(0);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState('scam');
  const [reportDetails, setReportDetails] = useState('');
  const [alsoBlock, setAlsoBlock] = useState(true);
  const [submittingReport, setSubmittingReport] = useState(false);

  // Re-render every second in a thread so the typing indicator expires
  useEffect(() => {
    if (!conversationId) return;
    const t = setInterval(() => setTypingTick(x => x + 1), 1000);
    return () => clearInterval(t);
  }, [conversationId]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Load conversation messages when viewing a specific conversation
  useEffect(() => {
    if (conversationId && isAuthenticated) {
      loadConversationMessages(conversationId);
    }
  }, [conversationId, isAuthenticated]);

  // Refresh conversations list when component mounts
  useEffect(() => {
    if (isAuthenticated && !conversationId) {
      refreshConversations();
    }
  }, [isAuthenticated, conversationId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, conversationId]);

  if (!isAuthenticated) {
    return (
      <Layout title="Messages">
        <div className="flex flex-col items-center justify-center py-20 px-4">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <Image className="w-8 h-8 text-gray-400" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Sign in to view messages</h2>
          <p className="text-gray-500 text-center mb-4">Connect with buyers and sellers</p>
          <Button onClick={() => navigate('/login')} className="bg-minted-600 hover:bg-minted-700">
            Sign In
          </Button>
        </div>
      </Layout>
    );
  }

  // Conversation list view
  if (!conversationId) {
    const userConversations = conversations.filter(
      c => c.participants?.includes(user?.id)
    );

    return (
      <Layout title="Messages">
        {userConversations.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 px-4">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <Image className="w-8 h-8 text-gray-400" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">No messages yet</h2>
            <p className="text-gray-500 text-center mb-4">Start a conversation by messaging a seller</p>
            <Button onClick={() => navigate('/')} className="bg-minted-600 hover:bg-minted-700">
              Browse Products
            </Button>
          </div>
        ) : (
          <div className="divide-y">
            {userConversations.map(conversation => {
              const otherUserId = conversation.participants?.find(id => id !== user?.id);
              const otherUser = getUserByIdSync(otherUserId);
              const product = products.find(p => p.id === (conversation.product_id || conversation.productId));
              const lastMessageTime = new Date(conversation.last_message_time || conversation.lastMessageTime);

              return (
                <button
                  key={conversation.id}
                  onClick={() => navigate(`/messages/${conversation.id}`)}
                  className="w-full flex items-center gap-3 p-4 hover:bg-gray-50 transition-colors text-left"
                >
                  <div className="relative">
                    <img
                      src={otherUser?.avatar || `https://ui-avatars.com/api/?name=User&background=00c978&color=fff`}
                      alt={otherUser?.name || 'User'}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    {(conversation.unread_count || conversation.unreadCount) > 0 && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-minted-600 text-white text-xs rounded-full flex items-center justify-center">
                        {conversation.unread_count || conversation.unreadCount}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <h3 className="font-medium text-gray-900 truncate">{otherUser?.name || 'User'}</h3>
                      <span className="text-xs text-gray-500">
                        {lastMessageTime.toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 truncate">{conversation.last_message || conversation.lastMessage}</p>
                    {product && (
                      <p className="text-xs text-minted-600 truncate mt-0.5">
                        Re: {product.title}
                      </p>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </Layout>
    );
  }

  // Single conversation view - handle both string and numeric IDs
  const conversation = conversations.find(c => c.id === conversationId || c.id === parseInt(conversationId) || String(c.id) === conversationId);
  if (!conversation) {
    return (
      <Layout title="Messages" showBack>
        <div className="flex flex-col items-center justify-center py-20">
          <p className="text-gray-500">Conversation not found</p>
        </div>
      </Layout>
    );
  }

  const otherUserId = conversation.participants?.find(id => id !== user?.id);
  const otherUser = getUserByIdSync(otherUserId);
  const product = products.find(p => p.id === (conversation.product_id || conversation.productId));
  // Filter messages for this conversation - handle both field name formats
  const conversationMessages = messages.filter(m => 
    m.conversation_id === conversation.id || 
    m.conversationId === conversation.id ||
    m.conversation_id === conversationId ||
    m.conversationId === conversationId
  );

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    await sendMessage(conversation.id, newMessage.trim(), otherUserId, conversation.product_id || conversation.productId);
    setNewMessage('');
  };

  const otherTyping = typingConversations?.[conversation.id] && (Date.now() - typingConversations[conversation.id] < 3000);

  const handleInputChange = (e) => {
    setNewMessage(e.target.value);
    const now = Date.now();
    if (now - lastTypingRef.current > 1500 && otherUserId) {
      lastTypingRef.current = now;
      sendTypingSignal(conversation.id, otherUserId);
    }
  };

  const handleBlock = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/users/${user.id}/block`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ blocked_user_id: otherUserId })
      });
      if (!res.ok) throw new Error('Block failed');
      toast.success(`${otherUser?.name || 'User'} blocked. They can no longer message you.`);
      navigate('/messages');
    } catch {
      toast.error('Could not block user. Please try again.');
    }
  };

  const handleReport = async () => {
    setSubmittingReport(true);
    try {
      const res = await fetch(`${API_BASE}/api/reports`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reporter_id: user.id,
          reported_user_id: otherUserId,
          conversation_id: conversation.id,
          reason: reportReason,
          details: reportDetails
        })
      });
      if (!res.ok) throw new Error('Report failed');
      toast.success('Report sent. Our team will review it.');
      setReportOpen(false);
      setReportDetails('');
      if (alsoBlock) await handleBlock();
    } catch {
      toast.error('Could not send report. Please try again.');
    } finally {
      setSubmittingReport(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Custom Header */}
      <header className="fixed top-0 left-0 right-0 bg-minted-600 text-white z-50 safe-area-top">
        <div className="max-w-lg mx-auto flex items-center h-14 px-4">
          <button onClick={() => navigate('/messages')} className="p-1 -ml-1 mr-2">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <button
            onClick={() => otherUserId && navigate(`/seller/${otherUserId}`)}
            className="flex items-center gap-3 flex-1"
          >
            <img
              src={otherUser?.avatar || `https://ui-avatars.com/api/?name=User&background=00c978&color=fff`}
              alt={otherUser?.name || 'User'}
              className="w-8 h-8 rounded-full object-cover border-2 border-white/30"
            />
            <div className="text-left">
              <h1 className="font-semibold text-sm">{otherUser?.name || 'User'}</h1>
              <p className="text-xs text-minted-100" data-testid="chat-status">{otherTyping ? 'typing…' : 'Active now'}</p>
            </div>
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="p-2 -mr-2 hover:bg-white/10 rounded-lg" data-testid="chat-options-btn">
                <MoreVertical className="w-5 h-5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setReportOpen(true)} data-testid="report-user-item">
                <Flag className="w-4 h-4 mr-2 text-amber-600" />
                Report user
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleBlock} className="text-red-600" data-testid="block-user-item">
                <Ban className="w-4 h-4 mr-2" />
                Block user
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        {/* Product reference */}
        {product && (
          <button
            onClick={() => navigate(`/product/${product.id}`)}
            className="bg-minted-700 px-4 py-2 flex items-center gap-2"
          >
            <img
              src={product.images[0]}
              alt={product.title}
              className="w-8 h-8 rounded object-cover"
            />
            <span className="text-xs text-minted-100 truncate flex-1 text-left">
              {product.title}
            </span>
            <span className="text-sm font-medium">${product.price}</span>
          </button>
        )}
      </header>

      {/* Messages */}
      <main className={`flex-1 overflow-y-auto px-4 py-4 pb-24`} style={{ paddingTop: product ? 'calc(7rem + env(safe-area-inset-top, 12px))' : 'calc(4rem + env(safe-area-inset-top, 12px))' }}>
        <div className="max-w-lg mx-auto space-y-3">
          {/* Safety notice */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5 flex items-start gap-2" data-testid="chat-safety-notice">
            <ShieldAlert className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-[11px] text-amber-800 leading-snug">
              <strong>Stay safe:</strong> real sales always appear in your Orders tab. Minted never sends payment confirmations or verification links in chat. Never click external links or share bank details.
            </p>
          </div>
          {conversationMessages.map((msg) => {
            const isMe = (msg.sender_id || msg.senderId) === user?.id;
            const msgTime = new Date(msg.timestamp);

            return (
              <div
                key={msg.id}
                className={`flex flex-col ${isMe ? 'items-end' : 'items-start'}`}
              >
                <div
                  className={`max-w-[75%] rounded-2xl px-4 py-2 ${
                    isMe
                      ? 'bg-minted-600 text-white rounded-br-md'
                      : 'bg-white text-gray-900 rounded-bl-md shadow-sm'
                  }`}
                >
                  <p className="text-sm">{msg.text}</p>
                  <p className={`text-[10px] mt-1 ${isMe ? 'text-minted-100' : 'text-gray-400'}`}>
                    {msgTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
                {msg.is_suspicious && !isMe && (
                  <div className="max-w-[75%] mt-1 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid={`scam-warning-${msg.id}`}>
                    <div className="flex items-start gap-2">
                      <ShieldAlert className="w-3.5 h-3.5 text-red-600 flex-shrink-0 mt-0.5" />
                      <p className="text-[11px] text-red-700 leading-snug">
                        <strong>Likely scam.</strong> This message contains an external link, email address or scam wording. Do not click links, email them, or "verify" anything.
                      </p>
                    </div>
                    <button
                      onClick={() => setReportOpen(true)}
                      className="mt-1.5 text-[11px] font-semibold text-red-700 underline underline-offset-2"
                      data-testid={`scam-report-btn-${msg.id}`}
                    >
                      Report &amp; block this user
                    </button>
                  </div>
                )}
              </div>
            );
          })}
          {otherTyping && (
            <div className="flex justify-start" data-testid="typing-indicator">
              <div className="bg-white rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
                <div className="flex gap-1">
                  <span className="w-2 h-2 bg-stone-300 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-2 h-2 bg-stone-300 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-2 h-2 bg-stone-300 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </main>

      {/* Message Input - pinned to the very bottom of the screen */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t px-4 py-3 z-50 safe-area-bottom" data-testid="chat-input-bar">
        <form onSubmit={handleSend} className="max-w-lg mx-auto flex gap-2">
          <Input
            value={newMessage}
            onChange={handleInputChange}
            placeholder="Type a message..."
            className="flex-1"
            data-testid="message-input"
          />
          <Button
            type="submit"
            size="icon"
            className="bg-minted-600 hover:bg-minted-700"
            disabled={!newMessage.trim()}
          >
            <Send className="w-5 h-5" />
          </Button>
        </form>
      </div>

      {/* Report Dialog */}
      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent className="max-w-sm rounded-2xl" data-testid="report-dialog">
          <DialogHeader>
            <DialogTitle>Report {otherUser?.name || 'this user'}</DialogTitle>
            <DialogDescription>Our team reviews every report. The user won't know who reported them.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {REPORT_REASONS.map(r => (
              <label key={r.value} className={`flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer text-sm ${reportReason === r.value ? 'border-emerald-700 bg-emerald-50' : 'border-stone-200'}`}>
                <input
                  type="radio"
                  name="report-reason"
                  value={r.value}
                  checked={reportReason === r.value}
                  onChange={() => setReportReason(r.value)}
                  className="accent-emerald-700"
                  data-testid={`report-reason-${r.value}`}
                />
                {r.label}
              </label>
            ))}
            <Textarea
              value={reportDetails}
              onChange={(e) => setReportDetails(e.target.value)}
              placeholder="Anything else we should know? (optional)"
              className="text-sm"
              rows={2}
              data-testid="report-details-input"
            />
            <label className="flex items-center gap-2 text-sm text-stone-600 pt-1">
              <input
                type="checkbox"
                checked={alsoBlock}
                onChange={(e) => setAlsoBlock(e.target.checked)}
                className="accent-emerald-700"
                data-testid="report-also-block"
              />
              Also block this user
            </label>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setReportOpen(false)}>Cancel</Button>
            <Button
              onClick={handleReport}
              disabled={submittingReport}
              className="bg-red-600 hover:bg-red-700"
              data-testid="submit-report-btn"
            >
              {submittingReport ? 'Sending...' : 'Send Report'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MessagesPage;